
import { Component, Vue, Ref } from 'vue-property-decorator'
import { registerNiceModalComponent } from '@xt/client/utils/messageModalQueue/index'
import type { TopicTipProps, GiftProps, CouponProps, IMessageResp } from '@xt/client/utils/messageModalQueue/types.d.ts'
import { EnumModalQueueItemType, EnumRegisterModalNames, EnumModalQueueItemCode } from '@xt/client/enums/messageModalQueue/index'
import { Observer } from 'mobx-vue'
import type { BaseCouponItem } from '@xt/client/types/coupon'
import { EnumApiNotice } from '@xt/client/api/notice'
import { $service } from '@xt/client/experimental/request/service'
import { includes } from 'lodash'

registerNiceModalComponent()

@Observer
@Component({
  components: {}
})
export default class MessageModalQueue extends Vue {
  hasReadList = new Set<string>()

  transformCouponProps(dataSource: IMessageResp[]): CouponProps {
    const list = dataSource
      .filter(i => i.noticeSubType === EnumModalQueueItemCode.Coupon && i.bizData && i.bizData.jsonContent)
      .map(j => JSON.parse(j.bizData!.jsonContent!) as BaseCouponItem)

    return {
      successList: list,
      failList: [],
      type: EnumModalQueueItemType.Coupon
    }
  }

  transformGiftProps(data: any): GiftProps | null {
    if (!data.bizData || !data.bizData.jsonContent) return null

    const parsed = JSON.parse(data.bizData!.jsonContent!) as GiftProps

    return {
      ...parsed,
      type: EnumModalQueueItemType.Gift
    }
  }

  mapTopicTipProps(
    parsed: {
      regionName: string
    },
    code: EnumModalQueueItemCode
  ) {
    let data: {
      title: string
      subTitle: string
      type: EnumModalQueueItemType
      description?: string
      backgroundUrl?: string
      hideCancel?: boolean
      confirmText?: string
      layoutClassName?: string
    } | null = null

    switch (code) {
      case EnumModalQueueItemCode.Init:
        data = {
          title: `${parsed.regionName}分桐`,
          type: EnumModalQueueItemType.Init,
          subTitle: '成功开启啦~',
          description: `恭喜${parsed.regionName}桐好，今后笔墨相伴，常赴雅约`,
          backgroundUrl: `${this.$store.$global.static}/images/topic/pc/${EnumModalQueueItemType.Init}-pop-bg.png`,
          layoutClassName: `${EnumModalQueueItemType.Init}-confirm-modal`
        }
        break
      case EnumModalQueueItemCode.Recruit:
        data = {
          title: `${parsed.regionName}分桐`,
          type: EnumModalQueueItemType.Recruit,
          subTitle: '助力成功',
          description: '分桐负责人招募开启',
          backgroundUrl: `${this.$store.$global.static}/images/topic/pc/${EnumModalQueueItemType.Recruit}-pop-bg.png`,
          layoutClassName: `${EnumModalQueueItemType.Recruit}-confirm-modal`
        }
        break
      case EnumModalQueueItemCode.Exit:
        data = {
          title: `您已退出${parsed.regionName}分桐`,
          type: EnumModalQueueItemType.Exit,
          subTitle: '',
          description: '如需加入其他分桐，可前往桐学会页面提交申请',
          backgroundUrl: '',
          hideCancel: true,
          confirmText: '确认',
          layoutClassName: `${EnumModalQueueItemType.Exit}-confirm-modal`
        }
        break
    }
    return data
  }

  transformTopicTipProps(data: IMessageResp, code: EnumModalQueueItemCode): TopicTipProps | null {
    if (!data.bizData || !data.bizData.jsonContent) return null

    const parsed = JSON.parse(data.bizData!.jsonContent!) as { regionName: string; guildName: string }

    const dataMap = this.mapTopicTipProps(parsed, code)

    // 匹配不到的类型不加入队列
    if (!dataMap) return null

    return {
      bizId: data.bizId,
      ...dataMap
    }
  }

  /** 拉取消息生成弹窗队列 */
  async initCongratulateModal() {
    if (!this.$store.$storeUser.UserInfo.id) return

    const res = await this.$store.$storeUser.messageNotice.onLoadPop()

    if (!res.length) return

    for (const item of res) {
      // 已读消息跳过
      if (this.hasReadList.has(item.id)) continue

      if (includes([EnumModalQueueItemCode.Init, EnumModalQueueItemCode.Recruit, EnumModalQueueItemCode.Exit], item.noticeSubType)) {
        const queueItem = this.transformTopicTipProps(item, item.noticeSubType)

        if (!queueItem) continue

        this.onReadNotice([item.id])
        await this.$niceModal.show(EnumRegisterModalNames.Topic, queueItem)
      } else if (item.noticeSubType === EnumModalQueueItemCode.Gift) {
        const dataSource = this.transformGiftProps(item)

        if (!dataSource) continue

        this.onReadNotice([item.id])
        await this.$niceModal.show(EnumRegisterModalNames.Gift, {
          dataSource
        })
      } else if (item.noticeSubType === EnumModalQueueItemCode.Coupon) {
        const data = this.transformCouponProps(res)

        if (!data) continue

        const ids = res.filter(i => i.noticeSubType === EnumModalQueueItemCode.Coupon).map(i => i.id)
        this.onReadNotice(ids)

        await this.$niceModal.show(EnumRegisterModalNames.Coupon, {
          data
        })
      }

      await this.$sleep(100) // 可选短延迟以避免 UI 闪烁
    }
  }

  async onReadNotice(ids: string[]) {
    const [, resp] = await $service<boolean>({
      url: EnumApiNotice.noticeBatchRead,
      method: 'post',
      params: {
        ids: ids.join(',')
      }
    })

    if (!resp) return

    ids.forEach(id => this.hasReadList.add(id))

    return resp === true
  }

  async created(): Promise<void> {
    await this.initCongratulateModal()
  }

  destroyed(): void {}
}
